import {
  getSignedDriftedWeight,
  getSignedWeight,
  type InvestorStrategyRecord,
  type StrategyEntry,
} from "../services/investorStrategyService";
import { resolveStrategyWeight } from "./strategyFlatPositions";
import type {
  ActiveContractCompositionEntry,
  BasketSearchSnapshot,
  PortfolioSearchSnapshot,
  ProductRestrictionFlag,
  ProductSearchItem,
} from "../types/productV3";
import { isNewSnapshotShape } from "../types/productV3";
import { hasProductRestrictions } from "./productRestrictions";
import { parseMinNlvTag, type MinNlvValue } from "../types/productMetadata";
import { formatCurrencyWithCommas } from "./numberUtils";
import { weightToWholeDollars } from "./strategyDollarInputMode";

// Includes MANAGED_STRATEGY so admin surfaces that render a managed holding as
// a first-class row can resolve its title through the same lookup every other
// strategy row uses. Investor product search never produces a managed entry, so
// investor callers see exactly what they saw before.
export type ProductLookupKey =
  `${"PORTFOLIO" | "BASKET" | "MANAGED_STRATEGY"}-${number}`;

export interface ProductLookupEntry {
  title: string;
  providerId: number;
  providerName: string;
  minNlv?: number;
  /**
   * The signed-in account's restriction flags for this product, as returned by
   * investor product search, carried so strategy surfaces can mark restricted
   * products without a second lookup. Optional: sources that do not carry flags
   * (provider/admin search) simply omit it, which reads as unrestricted.
   */
  restrictionFlags?: ProductRestrictionFlag[];
}

export function getStrategyDisplayName(
  entry: Pick<InvestorStrategyRecord, "entityType" | "productId">,
  lookup: Map<ProductLookupKey, ProductLookupEntry>
): string {
  const key: ProductLookupKey = `${entry.entityType}-${entry.productId}`;
  const product = lookup.get(key);
  if (product?.title) return product.title;
  return `${entry.entityType} #${entry.productId}`;
}

export function getStrategyViewUrl(
  entry: Pick<InvestorStrategyRecord, "entityType" | "productId">,
  lookup: Map<ProductLookupKey, ProductLookupEntry>
): string | null {
  // Discover has no managed-strategy detail route, so a managed holding has no
  // view URL. Returning null keeps the row un-linked rather than pointing at a
  // path that 404s.
  if (entry.entityType === "MANAGED_STRATEGY") return null;
  const key: ProductLookupKey = `${entry.entityType}-${entry.productId}`;
  const product = lookup.get(key);
  if (!product) return null;
  const type = entry.entityType.toLowerCase();
  return `/positions/discover/${type}/${product.providerId}/${entry.productId}`;
}

// Structural param, not StrategyEntry: a MANAGED_STRATEGY record carries the
// same weight/driftedWeight pair and drifts by the same arithmetic.
export function computeDrift(entry: Pick<StrategyEntry, "weight" | "driftedWeight">): number | null {
  if (entry.driftedWeight == null) return null;
  return entry.driftedWeight - parseFloat(entry.weight);
}

/**
 * The record's current weight with its direction reconciled against the
 * allocated weight's sign.
 *
 * A short can arrive as `weight: "-40"` with an UNSIGNED `driftedWeight: 42`.
 * Read raw, that current reads long: it inverts the displayed direction, and
 * inside a merge it cancels against a real long instead of adding to a short.
 * `resolveStrategyWeight` is the existing single definition of this rule --
 * reused here rather than restated.
 *
 * Returns null when no current weight is available (stale snapshot, no factor).
 */
export function getReconciledCurrentWeight(
  entry: Pick<StrategyEntry, "weight" | "driftedWeight">
): number | null {
  const current = getSignedDriftedWeight(entry);
  if (current === null) return null;
  return resolveStrategyWeight({ weight: getSignedWeight(entry), driftedWeight: current });
}

/** Drift measured against the reconciled current weight. */
export function computeReconciledDrift(
  entry: Pick<StrategyEntry, "weight" | "driftedWeight">
): number | null {
  const current = getReconciledCurrentWeight(entry);
  if (current === null) return null;
  return current - getSignedWeight(entry);
}

export function formatStrategyUpdateMethod(method: "POST" | "PUT" | "DELETE"): string {
  switch (method) {
    case "POST": return "Created";
    case "PUT": return "Updated";
    case "DELETE": return "Removed";
  }
}

export function formatStrategyDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return isoString;
  }
}

export interface StrategyDraftEntry {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  weight: number;
  displayName: string;
}

export function computeDraftDiffSummary(
  entries: StrategyDraftEntry[],
  savedEntries: StrategyDraftEntry[]
): string {
  if (entries.length === 0 && savedEntries.length === 0) return "";

  if (savedEntries.length === 0 && entries.length > 0) {
    const total = entries.reduce((s, e) => s + e.weight, 0);
    return `This will set your strategy to ${entries.length} product${entries.length === 1 ? "" : "s"} totaling ${total}%.`;
  }

  if (entries.length === 0 && savedEntries.length > 0) {
    return `This will remove all ${savedEntries.length} product${savedEntries.length === 1 ? "" : "s"} from your strategy.`;
  }

  const savedMap = new Map<string, number>();
  for (const s of savedEntries) {
    savedMap.set(`${s.entityType}-${s.productId}`, s.weight);
  }

  const entryMap = new Map<string, number>();
  for (const e of entries) {
    entryMap.set(`${e.entityType}-${e.productId}`, e.weight);
  }

  let added = 0;
  let updated = 0;
  let removed = 0;

  for (const [key, weight] of entryMap) {
    if (!savedMap.has(key)) {
      added++;
    } else if (savedMap.get(key) !== weight) {
      updated++;
    }
  }

  for (const key of savedMap.keys()) {
    if (!entryMap.has(key)) {
      removed++;
    }
  }

  const parts: string[] = [];
  if (added > 0) parts.push(`add ${added}`);
  if (updated > 0) parts.push(`update ${updated}`);
  if (removed > 0) parts.push(`remove ${removed}`);

  if (parts.length === 0) return "";

  return `This will ${parts.join(", and ")} product${added + updated + removed === 1 ? "" : "s"}.`;
}

export interface DraftDiffItem {
  displayName: string;
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  type: "added" | "removed" | "changed";
  fromWeight?: number;
  toWeight?: number;
}

export interface DraftDiff {
  added: DraftDiffItem[];
  removed: DraftDiffItem[];
  changed: DraftDiffItem[];
  isEmpty: boolean;
}

export function computeDraftDiff(
  entries: StrategyDraftEntry[],
  savedEntries: StrategyDraftEntry[]
): DraftDiff {
  const savedMap = new Map<string, StrategyDraftEntry>();
  for (const s of savedEntries) {
    savedMap.set(`${s.entityType}-${s.productId}`, s);
  }
  const entryMap = new Map<string, StrategyDraftEntry>();
  for (const e of entries) {
    entryMap.set(`${e.entityType}-${e.productId}`, e);
  }

  const added: DraftDiffItem[] = [];
  const removed: DraftDiffItem[] = [];
  const changed: DraftDiffItem[] = [];

  for (const e of entries) {
    const key = `${e.entityType}-${e.productId}`;
    const saved = savedMap.get(key);
    if (!saved) {
      added.push({ displayName: e.displayName, entityType: e.entityType, productId: e.productId, type: "added", toWeight: e.weight });
    } else if (saved.weight !== e.weight) {
      changed.push({ displayName: e.displayName, entityType: e.entityType, productId: e.productId, type: "changed", fromWeight: saved.weight, toWeight: e.weight });
    }
  }

  for (const s of savedEntries) {
    const key = `${s.entityType}-${s.productId}`;
    if (!entryMap.has(key)) {
      removed.push({ displayName: s.displayName, entityType: s.entityType, productId: s.productId, type: "removed", fromWeight: s.weight });
    }
  }

  return { added, removed, changed, isEmpty: added.length === 0 && removed.length === 0 && changed.length === 0 };
}

/**
 * A product being ADDED by a save that the signed-in account cannot fully
 * trade. Carries the lookup key plus the fields the tradability modal needs, so
 * one derivation can drive both the strategy editor and the Alpha allocations
 * flow. Structurally assignable to the tradability modal's product shape.
 */
export interface RestrictedNewProduct {
  key: ProductLookupKey;
  productType: "PORTFOLIO" | "BASKET";
  productId: number;
  title: string;
  restrictionFlags?: ProductRestrictionFlag[];
}

/**
 * Products being ADDED by this save (nonzero weight, not already saved) that the
 * account cannot fully trade, i.e. the ones product search flagged for the
 * signed-in profile. Warn-only -- callers surface it but never block the save.
 * Scoped to new adds via the saved-key test so a long-held restricted product
 * does not re-warn on every save. Shared by the strategy draft editor and the
 * Alpha allocations flow so both classify restrictions identically.
 */
export function computeRestrictedNewProducts(
  entries: Array<Pick<StrategyDraftEntry, "entityType" | "productId" | "weight" | "displayName">>,
  savedEntries: Array<Pick<StrategyDraftEntry, "entityType" | "productId">>,
  productLookup: Map<ProductLookupKey, ProductLookupEntry> | undefined
): RestrictedNewProduct[] {
  const savedKeys = new Set(savedEntries.map((s) => `${s.entityType}-${s.productId}`));
  const products: RestrictedNewProduct[] = [];
  for (const entry of entries) {
    if (entry.weight === 0) continue;
    const key = `${entry.entityType}-${entry.productId}` as ProductLookupKey;
    if (savedKeys.has(key)) continue;
    const product = productLookup?.get(key);
    if (!product) continue;
    if (!hasProductRestrictions(product.restrictionFlags)) continue;
    products.push({
      key,
      productType: entry.entityType,
      productId: entry.productId,
      title: product.title || entry.displayName,
      restrictionFlags: product.restrictionFlags,
    });
  }
  return products;
}

/**
 * The per-position minimum-investment warning shown when an allocation falls
 * below a product's recommended minimum. Recommendation only -- callers surface
 * it but never block the save. Returns null when the position meets the minimum
 * (or has no minimum / no weight). Single source of truth for the exact copy,
 * shared by the strategy editor and the Alpha allocations flow so the two never
 * drift; the wording is pinned by tests.
 */
export function getMinNlvShortfallWarning(
  weight: number,
  nlv: number | null | undefined,
  minNlv: number | null | undefined
): string | null {
  if (minNlv == null || nlv == null || !Number.isFinite(nlv) || weight === 0) return null;
  const allocation = weightToWholeDollars(weight, nlv);
  if (allocation >= minNlv) return null;
  if (nlv < minNlv) {
    return `Your account balance (${formatCurrencyWithCommas(nlv, 0)}) is below the minimum recommended investment (${formatCurrencyWithCommas(minNlv, 0)}) for this product. Some positions may not be bought, which can cause tracking error.`;
  }
  const minWeight = Math.ceil((minNlv / nlv) * 100);
  return `Your allocation (${formatCurrencyWithCommas(allocation, 0)}) is below the minimum recommended investment (${formatCurrencyWithCommas(minNlv, 0)}) for this product. Some positions may not be bought, which can cause tracking error. Minimum weight to meet it: ${minWeight}%.`;
}

export function buildProductLookup(
  items: { productType: "BASKET" | "PORTFOLIO" | "MANAGED_STRATEGY"; productId: number; title: string; providerId: number | null; providerName: string; settings?: object; restrictionFlags?: ProductRestrictionFlag[] | null }[]
): Map<ProductLookupKey, ProductLookupEntry> {
  const map = new Map<ProductLookupKey, ProductLookupEntry>();
  for (const item of items) {
    const key: ProductLookupKey = `${item.productType}-${item.productId}`;
    const minNlvTag = (item.settings as { min_nlv_tag?: MinNlvValue } | undefined)?.min_nlv_tag;
    map.set(key, {
      title: item.title,
      // A managed strategy has no provider. 0 is the "no provider" sentinel and
      // is never dereferenced: getStrategyViewUrl bails on managed before it
      // reads providerId.
      providerId: item.providerId ?? 0,
      providerName: item.providerName,
      minNlv: parseMinNlvTag(minNlvTag),
      restrictionFlags: item.restrictionFlags ?? undefined,
    });
  }
  return map;
}

// --- Gross position counting ---

export interface GrossPositionCounts {
  long: number;
  short: number;
  options: number;
}

function countDirections(contracts: ActiveContractCompositionEntry[]): { long: number; short: number } {
  let long = 0;
  let short = 0;
  for (const c of contracts) {
    if (c.weight > 0) long++;
    else if (c.weight < 0) short++;
  }
  return { long, short };
}

/**
 * Computes gross long/short stock counts and total option count for a product.
 * Long/short are stock-only (expanded through baskets for portfolios).
 * Options is the count of direct option contracts on the portfolio.
 */
export function computeGrossPositionCounts(
  item: ProductSearchItem,
  basketLookup: Map<number, ProductSearchItem>
): GrossPositionCounts {
  if (!isNewSnapshotShape(item.snapshot)) {
    return { long: 0, short: 0, options: 0 };
  }

  if (item.productType === "BASKET") {
    const snap = item.snapshot as BasketSearchSnapshot;
    const stockCounts = countDirections(snap.activated.contracts?.stocks ?? []);
    return { ...stockCounts, options: 0 };
  }

  const snap = item.snapshot as PortfolioSearchSnapshot;
  const directStocks = countDirections(snap.activated.contracts?.stocks ?? []);
  const directOptions = snap.activated.contracts?.options ?? [];

  let long = directStocks.long;
  let short = directStocks.short;

  for (const basketRef of snap.activated.baskets ?? []) {
    const basketItem = basketLookup.get(basketRef.basketId);
    if (!basketItem || !isNewSnapshotShape(basketItem.snapshot)) continue;
    const bSnap = basketItem.snapshot as BasketSearchSnapshot;
    const basketCounts = countDirections(bSnap.activated.contracts?.stocks ?? []);
    long += basketCounts.long;
    short += basketCounts.short;
  }

  return { long, short, options: directOptions.length };
}
